import {ComputedPageItem, PageItem, ActionType} from '@taopix/taopix-core/dist/types/types';
import {EditTextIcon, TextArea} from '@taopix/taopix-design-system';
import classNames from 'classnames';
import React, {useState, useMemo, Dispatch, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {useTaopix} from '../Context/TaopixContext';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {useExperience} from '../Experience/ExperienceProvider';
import ToolPaletteSection from '../ToolPalette/ToolPaletteSection';
import {BoxType, Common, CoreActions, CoreEvent, Numbers, NumBool} from '@taopix/taopix-core';
import {flightCheckText, formatBox, getText} from '../../Utils/Text';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import useKeyboardMode from '../../Hooks/useKeyboardMode';
import PaletteInformationMessage from './BoxSettings/PaletteInformationMessage';
import {TaopixBaseShape} from '@taopix/taopix-core/dist/types/Canvas/Shapes';

export type AccessibleTextEditingPaletteProps = {
  sectionKey: string;
  item: ComputedPageItem;
};

const AccessibleTextEditingPalette = ({sectionKey, item}: AccessibleTextEditingPaletteProps) => {
  const app = useTaopix();
  const ui = useUiState();
  const {t} = useTranslation(['*', 'boxpalettes']);
  const keyboardMode = useKeyboardMode();
  const {experience} = useExperience();

  const stateManager = window.Taopix.Core.get('stateManager');
  const page = stateManager.getPage(ui.state.pageNumber);

  const dispatch = useDispatch<Dispatch<ActionType<typeof CoreActions>>>();

  const [editableTextItems, setEditableTextItems] = useState<PageItem[]>();
  const [textEditingRef, setTextEditingRef] = useState<number>(-1);

  const showPalette = useMemo(() => {
    // If editable textboxes on page and keyboard mode then show palette
    if (!keyboardMode) {
      setEditableTextItems([]);
      return false;
    }

    if (!page) {
      setEditableTextItems([]);
      return false;
    }

    const items: PageItem[] = Object.values(page?.items);

    if (!items?.length) {
      setEditableTextItems([]);
      return false;
    }

    const editableTextItems = items
      .filter(
        item =>
          item.type === BoxType.Text &&
          item.selectable === NumBool.True &&
          item.protectcontents === NumBool.False &&
          !Common.isCalendarGridObject(item.name)
      )
      .sort((a, b) => {
        if (a.boxtop !== b.boxtop) {
          return Number(Numbers.sub(a.boxtop, b.boxtop)); // top to bottom
        }
        return Number(Numbers.sub(a.boxleft, b.boxleft)); // left to right
      });

    setEditableTextItems(editableTextItems);

    if (!editableTextItems.length) {
      setEditableTextItems([]);
      return false;
    }

    return true;
  }, [ui.state.pageNumber, item?.ref, keyboardMode, page?.items]);

  const wrapperClasses = classNames(
    'flex',
    'flex-col',
    'overflow-y-auto',
    'pb-lg',
    'px-md',
    'space-y-xs',
    'sm:h-mobilePaletteContentHeight',
    'lg:flex-1'
  );

  const onSubmitChanges = (updatedText: string, ref: number) => {
    const deepClone = app.core.get('utils').deepClone;
    const currentPageClone = deepClone(stateManager.getPage(ui.state.pageNumber));
    const newPageItem = currentPageClone.items[ref];
    newPageItem.text = updatedText;

    const shape = app.core
      .get('instances')
      .get(ui.state.pageNumber)
      ?.getPageShapes()
      .find(shape => shape.attrs.item.ref === ref) as TaopixBaseShape;

    if (!shape) return;

    shape.updateProcessingOverlay(true);

    const formattedText = formatBox(newPageItem);

    flightCheckText(formattedText).then(flightCheckResult => {
      const flightCheckTextResult = flightCheckResult[0];

      const updatedPageItem = {
        ...newPageItem,
        styledtext: {
          ...newPageItem.styledtext,
          autofitmode: formattedText['box'].autofitmode,
          bassdata: flightCheckTextResult.vectordata,
          data: formattedText['box'].data,
          textoverflowed: flightCheckTextResult.textoverflowed ? 1 : 0,
          missingfonts: flightCheckTextResult.missingfonts,
          missingglyphfonts: flightCheckTextResult.missingglyphfonts,
          autofitscale: flightCheckTextResult.autofitscale,
        },
      };

      currentPageClone.items[ref] = updatedPageItem;

      dispatch(CoreActions.updatePage(currentPageClone));

      PubSub.publish(`${CoreEvent.RELOAD_PAGE}.${currentPageClone.pagenumber}`);

      shape.updateProcessingOverlay(false);
      setTextEditingRef(-1);
    });
  };

  return (
    <ToolPaletteSection
      sectionKey={sectionKey}
      title={t('boxpalettes:str_LabelEditText')}
      buttonIcon={<EditTextIcon />}
      showButton={showPalette}
      id={'accessible-text-editing-palette'}
      data-testid={'accessible-text-editing-palette'}
      buttonLabel={t('boxpalettes:str_LabelEditText')}
      useFocusMode
    >
      <div className={wrapperClasses}>
        {editableTextItems && editableTextItems.length > 0 ? (
          editableTextItems.map((item, index) => {
            const label =
              Common.getLocalisedLabel(item.labelshort, true).text ||
              `${t('toolpalette:str_LabelTextBox', {defaultValue: 'Text box'})} ${index + 1}`;
            return (
              <div key={`edit-text-wrapper-${item.ref}`} className="pt-md">
                <TextArea
                  id={`edit-text-${item.ref}`}
                  key={`edit-text-${item.ref}`}
                  name={'edit-text'}
                  label={label}
                  data-testid={'accessible-edit-text-textarea'}
                  className={'lg:min-h-[100rem] sm:min-h-[60rem] w-full'}
                  style={{resize: 'none'}}
                  value={getText(item.styledtext.data)}
                  onChange={event => {
                    setTextEditingRef(item.ref);
                    onSubmitChanges(event.target.value, item.ref);
                  }}
                  debounceDelay={500}
                  disabled={textEditingRef !== -1 && textEditingRef !== item.ref} // disable any other text areas while the current one is processing
                />
              </div>
            );
          })
        ) : (
          <PaletteInformationMessage
            message={t('toolpalette:str_MessageNoTextBoxesOnCurrentPage', {
              defaultValue: 'There are no text boxes on the current page',
            })}
          ></PaletteInformationMessage>
        )}
      </div>
    </ToolPaletteSection>
  );
};

export default AccessibleTextEditingPalette;
